import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Box,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Refresh as RefreshIcon,
  AddLink as LinkIcon,
  Hub as HubIcon,
  OpenInNew as OpenInNewIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
// CREDIT_TYPE_LABELS is no longer rendered here — see the commented-out
// breakdown row on the "Credits Remaining" card.
import { useCreditBalances /*, CREDIT_TYPE_LABELS */ } from '../hooks/useCreditBalances';
import { creditsSummaryMessage } from '../utils/creditRates';
import { usePieceEstimate } from '../hooks/usePieceEstimate';
import { isWebsiteConnected, websiteStatusLabel } from '../utils/websiteStatus';
import { websiteHref, websiteLabel } from '../utils/websiteUrl';
import { websiteLimitMessage, isWebsiteLimitError } from '../utils/websiteLimitCopy';
import { deriveFreeTierState } from '../utils/subscriptionState';
import RichMessage from './RichMessage';
import { QUICK_START_PATH, PLANS_PATH } from '../utils/routes';
import { useWebsitesWithStatus } from '../hooks/useWebsitesWithStatus';

const ORANGE = '#E55432';
const ORANGE_GRADIENT = 'linear-gradient(135deg, #BF3A1A 0%, #E86340 100%)';
const TEAL_DARK = '#0E6059';
const TEAL = '#1C7C73';
const TEAL_LIGHT = '#E8F3F1';
const CARD_BG = '#ECEDE4';
const TEXT = '#1A1A1A';
const TEXT_MUTED = '#8A8F85';
const CARD_SHADOW = '0px 4px 4px 0px rgba(0,0,0,0.25)';

// Website Details | Status | Site Code | Date Added | Actions.
// Declared once so the header row and the body rows can never drift apart.
const GRID_COLUMNS = '1fr 130px 150px 120px 90px';

const getDomainInitial = (domain = '') => domain.replace(/^www\./, '').charAt(0).toUpperCase();

/**
 * The one definition of what a connection status looks like.
 *
 * Read by BOTH the status chip and the row avatar. That is the whole point: the
 * avatar used to be tinted by `getDomainColor`, which indexed a pastel palette
 * by the first character of the domain — decorative, and unrelated to anything
 * on the row. It now carries the status, so a red avatar means the same thing
 * as a red chip at a glance.
 *
 * Defined once because "they should match" is only true for as long as there is
 * a single source for them; two copies of the hex values agree until the first
 * time one is edited.
 */
const STATUS_COLORS = {
  connected: { fg: '#006A63', bg: 'rgba(0,106,99,0.10)' },
  disconnected: { fg: '#A7391E', bg: 'rgba(167,57,30,0.10)' },
};
const statusColors = (connected) => (connected ? STATUS_COLORS.connected : STATUS_COLORS.disconnected);

// OLD: const WebsiteManagement = ({ onUpgradePlan }) => {
// `onUpgradePlan` was never passed — Dashboard renders <WebsiteManagement />
// bare — so it was always undefined and calling it would have thrown. The
// Upgrade button in the limit dialog navigates directly, like everything else
// in the app that links to the plans grid.
const WebsiteManagement = () => {
  const { userData } = useAuth();
  const navigate = useNavigate();
  const [rawWebsites, setRawWebsites] = useState([]);
  // Connection status is inferred CMS-side from user_generations (see
  // hooks/useWebsitesWithStatus). Same hook as the Overview "My Websites" card,
  // so the two screens cannot disagree.
  // OLD: const websites = useWebsitesWithStatus(userData?.id, rawWebsites);
  //      (returned a bare array back when the server supplied last_connected_at
  //       and status needed no second query)
  const { websites, statusReady } = useWebsitesWithStatus(userData?.id, rawWebsites);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // True only when `error` describes the URL the user typed, so the field is highlighted
  // for typos but not for plan limits or server failures.
  const [isUrlError, setIsUrlError] = useState(false);
  const [success, setSuccess] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingWebsite, setDeletingWebsite] = useState(null);
  const [newDomain, setNewDomain] = useState('');
  const [addingWebsite, setAddingWebsite] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);
  // Which code was just copied — drives the ✓ feedback on the copy button.
  const [copiedCode, setCopiedCode] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  // ── The website-limit gate ────────────────────────────────────────────────
  // Shown INSTEAD of the add form when the account is already at its plan's
  // allowance. It used to be a server error the customer only met after typing
  // a url and pressing the button — a refusal for a condition that was knowable
  // before they were invited to type anything.
  const [limitDialogOpen, setLimitDialogOpen] = useState(false);
  // The live tiers, for naming what an upgrade actually allows. Fetched lazily
  // on first open: the sentence is the only thing that needs them, and most
  // sessions never see this dialog.
  const [plans, setPlans] = useState(null);
  // Same live credit_balances source as the Overview "Available Credits" card.
  // byType is no longer rendered as a breakdown here (single number, matching
  // Overview) but still feeds the "≈ N pieces" line, which needs per-type data.
  // One pooled balance now — the per-type breakdown went with the pool merge.
  const { total: creditsTotal , pricing } = useCreditBalances(userData?.id);
  // "≈ N pieces" — from the server's one estimate endpoint, shared with the plugin.
  const creditPieces = usePieceEstimate(creditsTotal);
  const creditPiecesMessage = creditsSummaryMessage(creditsTotal, creditPieces, pricing);

  // ── One entry point for "Add Website" ─────────────────────────────────────
  // The header button, the footer link and the empty-state button all call
  // this, so the limit gate cannot be bypassed through whichever control
  // someone forgets about. Below the limit it opens the form exactly as before.
  //
  // `website_limit` comes from /users/by-email and is NULL for the unlimited
  // tier — so the check is written against a real number only, and an account
  // whose limit we could not read gets the form and the server's answer, which
  // is the old behaviour rather than a wrongly locked door.
  const limit = Number(userData?.website_limit);
  const atWebsiteLimit = Number.isFinite(limit) && limit > 0 && websites.length >= limit;

  // Opening the limit dialog, from either of the two ways of reaching it: the
  // click-time gate below, and the server refusing an add that got past it.
  const showLimitDialog = () => {
    setLimitDialogOpen(true);
    // Best effort. The upgrade sentence falls back to the static tier ladder
    // without these, so a failed or slow request costs nothing visible — which
    // is why nothing waits on it.
    if (!plans) {
      api.getSubscriptionPlans(userData?.id)
        .then((res) => setPlans(res?.plans ?? null))
        .catch(() => { /* the copy stands on its own */ });
    }
  };

  const openAddWebsite = () => {
    if (atWebsiteLimit) {
      showLimitDialog();
      return;
    }
    setAddDialogOpen(true);
  };

  // Built on every render rather than at open time so it picks up the plans as
  // soon as they arrive, without a second piece of state to keep in step.
  //
  // `trialExpired` is what separates "One website only on the free trial" from
  // "One website only on the Basic plan" — the same plan row, two states, and the
  // shared derivation the Dashboard badge uses so the two cannot disagree.
  const limitCopy = websiteLimitMessage({
    limit,
    planSlug: userData?.plan_slug,
    planName: userData?.plan_name,
    trialExpired: deriveFreeTierState(userData).trialExpired,
    domains: websites.map((w) => w.domain),
    plans,
  });

  const resetAddDialog = () => {
    setAddDialogOpen(false);
    setNewDomain('');
    setError('');
    setIsUrlError(false);
  };

  // `silent` re-reads without blanking the table behind a spinner — used by the
  // manual refresh and the credential-change effect below.
  const loadWebsites = async (silent = false) => {
    if (!userData?.id) return;
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const response = await api.getUserWebsites(userData.id);
      setRawWebsites(response.data || []);
    } catch (err) {
      // /websites is the one page here that needs a live Supabase session, so
      // it is where an expired one surfaces first — and "Failed to load
      // websites" sent people looking for a bug in their websites. Say which
      // problem it actually is; the interceptor sets isAuthExpired only after
      // trying to refresh the session, so by this point signing in again really
      // is the fix.
      setError(err?.isAuthExpired
        ? 'Your session has expired. Please sign in again to see your websites.'
        : 'Failed to load websites');
      console.error(err);
    } finally {
      if (silent) setRefreshing(false); else setLoading(false);
    }
  };

  useEffect(() => {
    loadWebsites();
    // Keyed on the id (not the object) so auth-token refreshes on tab focus,
    // which produce a new userData reference, don't re-trigger a full reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  // E2 — never show a stale Site Code. Regenerating credentials in Settings
  // swaps the account's api_key, so a change to it is the signal that the codes
  // on screen may no longer be current: re-read the list from the server rather
  // than keeping whatever was fetched on mount. (Skipped on the first run —
  // the mount effect above has already loaded it.)
  const lastApiKey = useRef(userData?.api_key);
  useEffect(() => {
    if (!userData?.id) return;
    if (lastApiKey.current === userData?.api_key) return;
    lastApiKey.current = userData?.api_key;
    loadWebsites(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.api_key]);

  const handleAddWebsite = async () => {
    if (!newDomain.trim() || !userData?.id) return;
    setAddingWebsite(true);
    setError('');
    setIsUrlError(false);
    try {
      let domain = newDomain.trim();
      if (!domain.startsWith('http://') && !domain.startsWith('https://')) {
        domain = 'https://' + domain;
      }
      const url = new URL(domain);
      const response = await api.addWebsite({ domain: url.origin, user_id: userData.id });
      if (response.success) {
        setSuccess('Website added successfully!');
        resetAddDialog();
        loadWebsites();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      // ── The limit, refused by the server ──────────────────────────────────
      // The click-time gate reads `website_limit` off the cached session, so a
      // session that predates a downgrade — or predates the field — reaches the
      // form and is refused here instead. That refusal used to land in the red
      // alert as the server's own sentence, "Website limit reached. Your plan
      // allows 1 websites.", which is exactly the wording the dialog exists to
      // replace. Same condition, same message, whichever path finds it.
      if (isWebsiteLimitError(err)) {
        resetAddDialog();
        showLimitDialog();
        return;
      }
      // Only mark the URL field itself as invalid when the URL really is the problem.
      // Other server-side refusals are not input errors — reddening the field there
      // makes a server problem look like a typo, and the Alert below already says
      // what actually happened. Those get the Alert only.
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.message?.includes('Invalid URL')) {
        setError('Please enter a valid website URL');
        setIsUrlError(true);
      } else {
        setError('Failed to add website');
      }
    } finally {
      setAddingWebsite(false);
    }
  };

  const confirmDelete = (website) => {
    setDeletingWebsite(website);
    setDeleteDialogOpen(true);
  };

  const handleDeleteWebsite = async () => {
    if (!deletingWebsite) return;
    setDeletingId(deletingWebsite.id);
    try {
      await api.deleteWebsite(deletingWebsite.id);
      setDeleteDialogOpen(false);
      setDeletingWebsite(null);
      setSuccess('Website deleted successfully!');
      loadWebsites();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete website');
    } finally {
      setDeletingId(null);
    }
  };

  const copyToClipboard = async (text, label) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch (_) {
      // Clipboard API is unavailable on insecure origins and in some embedded
      // browsers — say so instead of silently claiming success.
      setError(`Could not copy the ${label.toLowerCase()}. Select it and copy manually.`);
      return;
    }
    setCopiedCode(text);
    setTimeout(() => setCopiedCode((c) => (c === text ? null : c)), 2000);
    setSuccess(`${label} copied!`);
    setTimeout(() => setSuccess(''), 2000);
  };

  const totalDomains = websites.length;

  // Wait for the status query on the FIRST load too, so rows never paint
  // "Not Connected" and then flip to Active. `statusReady` stays true across
  // later refetches, so a silent refresh can't blank the table.
  if (loading || !statusReady) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 12 }}>
        <CircularProgress sx={{ color: TEAL }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ borderRadius: '12px' }}>{error}</Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ borderRadius: '12px' }}>{success}</Alert>
      )}

      {/* Page Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 32, lineHeight: 1.1, letterSpacing: '-0.02em', color: TEXT }}>
            Website Management
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: 15, color: TEXT_MUTED, mt: 0.75, maxWidth: 520 }}>
            Connect your WordPress websites to your ImproveSEO account.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, alignSelf: 'flex-start' }}>
        {/* E2 — an explicit way to re-read Status + Site Code from the server,
            for the case where the code was regenerated in another tab. */}
        <Tooltip title="Refresh status & site codes">
          <span>
            <IconButton
              onClick={() => loadWebsites(true)}
              disabled={refreshing}
              sx={{ border: '1px solid #E5E7E0', borderRadius: '10px', p: 1.25, color: TEXT_MUTED, '&:hover': { color: TEAL, bgcolor: TEAL_LIGHT } }}
            >
              {refreshing ? <CircularProgress size={18} sx={{ color: TEAL }} /> : <RefreshIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </span>
        </Tooltip>
        <Button
          startIcon={<AddIcon sx={{ fontSize: 18 }} />}
          onClick={openAddWebsite}
          sx={{
            background: ORANGE_GRADIENT,
            color: '#FFFFFF',
            borderRadius: '10px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: 14,
            px: 3,
            py: 1.375,
            textTransform: 'none',
            boxShadow: '0 4px 14px rgba(190,58,26,0.30)',
            '&:hover': { background: ORANGE_GRADIENT, filter: 'brightness(1.08)' },
          }}
        >
          Add Website
        </Button>
        </Box>
      </Box>

      {/* Stats Row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 3 }}>

        <Box sx={{ bgcolor: '#FFFFFF', border: '1px solid #E5E7E0', borderRadius: '16px', p: '24px 28px', boxShadow: CARD_SHADOW }}>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: TEXT_MUTED, mb: 1 }}>
            Total Websites
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
            <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 44, lineHeight: 1, color: TEXT }}>
              {totalDomains}
            </Typography>
            <Box sx={{ bgcolor: '#F0F2EE', borderRadius: '9999px', px: 1.5, py: '3px' }}>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 12, color: TEXT_MUTED }}>
                +{Math.max(0, totalDomains)} this month
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ bgcolor: '#FFFFFF', border: '1px solid #E5E7E0', borderRadius: '16px', p: '24px 28px', boxShadow: CARD_SHADOW }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: TEXT_MUTED }}>
              API Key
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title={showApiKey ? 'Hide key' : 'Show key'}>
                <IconButton size="small" onClick={() => setShowApiKey((v) => !v)} sx={{ p: '4px', color: TEXT_MUTED, '&:hover': { color: TEAL, bgcolor: TEAL_LIGHT } }}>
                  {showApiKey ? <VisibilityOffIcon sx={{ fontSize: 16 }} /> : <VisibilityIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Copy API key">
                <IconButton size="small" onClick={() => copyToClipboard(userData?.api_key, 'API key')} sx={{ p: '4px', color: TEXT_MUTED, '&:hover': { color: TEAL, bgcolor: TEAL_LIGHT } }}>
                  <CopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <TextField
            fullWidth
            size="small"
            variant="standard"
            type={showApiKey ? 'text' : 'password'}
            value={userData?.api_key || ''}
            placeholder="—"
            InputProps={{
              readOnly: true,
              disableUnderline: true,
              sx: { fontFamily: 'monospace', fontWeight: 700, fontSize: 17, color: TEXT },
            }}
          />
        </Box>

        <Box sx={{ bgcolor: TEAL_DARK, borderRadius: '16px', p: '24px 28px', boxShadow: CARD_SHADOW, position: 'relative', overflow: 'hidden' }}>
          <Box sx={{ position: 'absolute', width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.07)', top: -30, right: -20, pointerEvents: 'none' }} />
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.65)', mb: 1 }}>
            Credits Remaining
          </Typography>
          {/* Single total, matching the Overview "Available Credits" card —
              same live credit_balances source (useCreditBalances), so the two
              can never disagree. Overview was reverted to one number per client
              feedback; this card follows so the dashboard is consistent.

              ── REPLACED: total + per-type breakdown row ───────────────────
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 32, lineHeight: 1, color: '#FFFFFF', flexShrink: 0 }}>
              {creditsTotal !== null ? creditsTotal.toLocaleString() : '—'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, overflow: 'hidden' }}>
              {Object.entries(CREDIT_TYPE_LABELS).map(([type, label], i) => (
                <Box key={type} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  {i > 0 && <Box sx={{ width: '1px', height: 22, bgcolor: 'rgba(255,255,255,0.2)', flexShrink: 0 }} />}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 15, lineHeight: '18px', color: '#FFFFFF' }}>
                      {creditsByType[type].toLocaleString()}
                    </Typography>
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 9, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)', whiteSpace: 'nowrap' }}>
                      {label}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
              ── END REPLACED ──────────────────────────────────────────────── */}
          {/* 2.3 — the number now carries its unit. "currency" in the feedback
              was ambiguous (credit unit vs. monetary value); the unit label is
              implemented as the default — see the note in the summary. */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
            <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 32, lineHeight: 1, color: '#FFFFFF' }}>
              {creditsTotal !== null ? creditsTotal.toLocaleString() : '—'}
            </Typography>
            {creditsTotal !== null && (
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 13, lineHeight: 1, color: 'rgba(255,255,255,0.75)' }}>
                {creditsTotal === 1 ? 'credit' : 'credits'}
              </Typography>
            )}
          </Box>
          {/* 2.3 — content equivalence. N is COMPUTED by the shared helper from
              the real per-post cost (creditRates.js, itself derived from the
              server's validateCredits guards) — never a hardcoded ratio.
              OLD: `≈ N pieces of SEO content with AI Images` */}
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: 11, lineHeight: '15px', color: 'rgba(255,255,255,0.65)', mt: 0.75 }}>
            {/* All three cases — none, exactly one, many — come from the shared
                helper. The zero case used to be worded here and explained a
                lopsided content-vs-image balance, which stopped being true once
                the pools merged into one. */}
            {creditsTotal === null ? ' ' : creditPiecesMessage}
          </Typography>
        </Box>
      </Box>

      {/* Website List Card */}
      <Box sx={{ bgcolor: '#FFFFFF', border: '1px solid #E5E7E0', borderRadius: '20px', boxShadow: CARD_SHADOW, overflow: 'hidden' }}>

        {/* Table header
            OLD (Status column removed):
            gridTemplateColumns: '1fr 130px 130px 90px'
            ['Website Details', 'Site Code', 'Date Added', 'Actions']
            Status is back per client feedback, and Site Code now sits AFTER it. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: GRID_COLUMNS, gap: 1, px: 3, py: 1.75, borderBottom: '1px solid #F0F0EA' }}>
          {['Website Details', 'Status', 'Site Code', 'Date Added', 'Actions'].map((h) => (
            <Typography
              key={h}
              sx={{
                fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 11,
                letterSpacing: '0.08em', textTransform: 'uppercase', color: TEXT_MUTED,
                // 2.2 — the header follows the centred icon group below it.
                textAlign: h === 'Actions' ? 'center' : 'left',
              }}
            >
              {h}
            </Typography>
          ))}
        </Box>

        {websites.length > 0 ? (
          <>
            {websites.map((website, i) => {
              // OLD: const isActive = website.active !== false;
              // `active` is the SOFT-DELETE flag and the list endpoint already
              // filters to active = true, so this was always true. The real
              // check is whether the plugin has verified the connection.
              const connected = isWebsiteConnected(website);
              const domainDisplay = websiteLabel(website.domain);
              // const hideStatus = domainDisplay.replace(/^www\./, '').toLowerCase() === 'google.com'; // one-off demo hack, not restored
              const initial = getDomainInitial(domainDisplay);
              // Same pair the status chip below uses, so the avatar and the chip
              // cannot disagree about what this row's state is.
              const colors = statusColors(connected);
              // Absolute, because domains are stored as bare hostnames and a
              // bare hostname in an href is a RELATIVE url — which is why this
              // icon used to open the CMS dashboard. See utils/websiteUrl.
              const href = websiteHref(website.domain);
              return (
                <Box
                  key={website.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: GRID_COLUMNS,
                    gap: 1,
                    alignItems: 'center',
                    px: 3,
                    py: 2,
                    borderBottom: i < websites.length - 1 ? '1px solid #F5F5F0' : 'none',
                    '&:hover': { bgcolor: '#FAFAF8' },
                    transition: 'background 0.12s',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      data-testid={`website-avatar-${website.id}`}
                      sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: colors.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                    >
                      <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 14, color: colors.fg }}>
                        {initial}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 14, color: TEXT }}>
                        {domainDisplay}
                      </Typography>
                      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: 12, color: TEXT_MUTED }}>
                        {website.description || 'SEO Content Platform'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* ── Status ── "Active" once the plugin has authenticated for
                      this site (or it has generated content); otherwise
                      "Not Connected". Same check as the Overview My Websites
                      card (utils/websiteStatus.js).

                      Copy is verbatim from client feedback (Aug 8). The
                      "Not Connected" wording points at the Connect Website icon,
                      which is safe: that icon renders only while !connected
                      (see the Actions cell below), i.e. exactly when this
                      tooltip is the one showing.

                      OLD copy described the generations-only rule, which no
                      longer matches the behaviour:
                        connected    → 'This site has generated content through the ImproveSEO plugin, so the connection is working.'
                        !connected   → 'No content has been generated for this site yet. If you have just connected the plugin, this turns Active after your first generation — …' */}
                  <Box>
                    <Tooltip
                      title={
                        connected
                          ? 'All set. To create content, head over to the backend of your website and navigate to the ImproveSEO plugin.'
                          : "To connect this website click on the 'Connect Website' icon under 'Actions' and follow the instructions."
                      }
                    >
                      <Box
                        component="span"
                        data-testid={`website-status-${website.id}`}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.625,
                          // Shared with the avatar on this row — see STATUS_COLORS.
                          bgcolor: colors.bg,
                          color: colors.fg,
                          borderRadius: '9999px',
                          px: 1.25,
                          py: '4px',
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 700,
                          fontSize: 11,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor', flexShrink: 0 }} />
                        {websiteStatusLabel(website)}
                      </Box>
                    </Tooltip>
                  </Box>

                  {/* ── Site Code (API code) — now AFTER Status ── */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                    <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 13, color: TEXT, letterSpacing: '0.03em', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {website.site_code || '—'}
                    </Typography>
                    {website.site_code && (
                      <Tooltip title={copiedCode === website.site_code ? 'Copied!' : 'Copy site code'}>
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(website.site_code, 'Site code')}
                          sx={{ p: '3px', flexShrink: 0, color: copiedCode === website.site_code ? TEAL : TEXT_MUTED, '&:hover': { color: TEAL, bgcolor: TEAL_LIGHT } }}
                        >
                          {copiedCode === website.site_code
                            ? <CheckIcon sx={{ fontSize: 14 }} />
                            : <CopyIcon sx={{ fontSize: 14 }} />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: 13, color: TEXT_MUTED }}>
                    {new Date(website.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </Typography>

                  {/* 2.2 — the icon group is centred at the CELL level, so the
                      column stays aligned whether a row has two icons or three.
                      Doing it here (rather than padding per case) means adding
                      or removing an action can never shift the column.
                      OLD: <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}> */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    {/* 2.1 — Connect Website. Rendered FIRST and only while the
                        site is unconnected; a connected row keeps its original
                        two icons. Deep-links to the Quick Start Guide with the
                        domain prefilled (?domain=…) rather than a bare tab link,
                        so the user lands on the automatic-setup step with their
                        site already filled in. */}
                    {!connected && (
                      <Tooltip title="Connect Website">
                        <IconButton
                          size="small"
                          aria-label="Connect Website"
                          onClick={() => navigate(`${QUICK_START_PATH}?domain=${encodeURIComponent(domainDisplay)}`)}
                          sx={{ p: '5px', color: ORANGE, '&:hover': { color: '#BF3A1A', bgcolor: '#FEF0EA' } }}
                        >
                          <LinkIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {/* `href` is built by websiteHref, NOT taken from the row.
                        Domains are stored as bare hostnames, and a bare
                        hostname in an href is relative — the browser resolved
                        it against the CMS and opened the dashboard instead of
                        the customer's site. A row we cannot build a url for
                        renders the icon disabled rather than a link that goes
                        nowhere; without the href MUI would render a plain
                        <a> that navigates in place. */}
                    <Tooltip title={href ? 'Open website' : 'No address on file for this site'}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label="Open website"
                          component="a"
                          href={href ?? undefined}
                          disabled={!href}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ p: '5px', color: TEXT_MUTED, '&:hover': { color: TEAL, bgcolor: TEAL_LIGHT } }}
                        >
                          <OpenInNewIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {/* "Remove Website", matching the dialog it opens — which is
                        already titled Remove Website, with Remove / Keep
                        Website buttons. Removal is a soft delete server-side,
                        so "remove" is the accurate word as well as the
                        consistent one. The aria-label moves with the tooltip:
                        announcing "Delete" while the tooltip says Remove is the
                        same defect one layer down. */}
                    <Tooltip title="Remove Website">
                      <IconButton size="small" aria-label="Remove Website" onClick={() => confirmDelete(website)} disabled={deletingId === website.id} sx={{ p: '5px', color: TEXT_MUTED, '&:hover': { color: '#DC2626', bgcolor: '#FEE2E2' } }}>
                        {deletingId === website.id ? <CircularProgress size={14} /> : <DeleteIcon sx={{ fontSize: 15 }} />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })}

            {/* Footer holds only the add link now that the promo copy is gone, so it is
                right-aligned rather than space-between (which would push it left). */}
            <Box sx={{ px: 3, py: 2.5, borderTop: '1px solid #F0F0EA', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 2 }}>
              <Typography component="span" onClick={openAddWebsite} sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 13, color: ORANGE, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                + Add Website
              </Typography>
            </Box>
          </>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10, px: 4, gap: 2.5 }}>
            <Box sx={{ width: 72, height: 72, borderRadius: '9999px', bgcolor: CARD_BG, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <HubIcon sx={{ fontSize: 36, color: TEXT_MUTED }} />
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 20, color: TEXT }}>
                Grow your network
              </Typography>
            </Box>
            {/* Was an inert <Typography> with no handler — now actually opens
                the Quick Start Guide (renamed from Tutorial). */}
            <Typography
              component={RouterLink}
              to={QUICK_START_PATH}
              sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 14, color: ORANGE, cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              View connection guide
            </Typography>
            <Button
              onClick={openAddWebsite}
              sx={{
                background: ORANGE_GRADIENT,
                color: '#FFFFFF',
                borderRadius: '10px',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: 14,
                px: 4,
                py: 1.375,
                textTransform: 'none',
                boxShadow: '0 4px 14px rgba(190,58,26,0.30)',
                mt: 0.5,
                '&:hover': { background: ORANGE_GRADIENT, filter: 'brightness(1.08)' },
              }}
            >
              Add Your First Website
            </Button>
          </Box>
        )}
      </Box>

      {/* Add Website Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={resetAddDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}
      >
        <DialogTitle sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 20 }}>
          Add New Website
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: TEXT_MUTED, mb: 2 }}>
            Enter the URL of the WordPress website you want to connect to your ImproveSEO account.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Website URL"
            type="url"
            variant="outlined"
            value={newDomain}
            onChange={(e) => { setNewDomain(e.target.value); setError(''); setIsUrlError(false); }}
            placeholder="https://yourwebsite.com"
            // No helper text. It read "Enter the full URL including https://",
            // which is a requirement handleAddWebsite deliberately removes: a
            // bare yourwebsite.com has the scheme prepended before it is parsed.
            // So the hint asked for work the field does not need and named the
            // only way to get it "wrong" as the thing that in fact works. The
            // placeholder still shows the shape without demanding it.
            error={isUrlError}
            onKeyDown={(e) => e.key === 'Enter' && handleAddWebsite()}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
          />
          {error && <Alert severity="error" sx={{ mt: 2, borderRadius: '10px' }}>{error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
          <Button onClick={resetAddDialog} sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleAddWebsite}
            disabled={!newDomain.trim() || addingWebsite}
            sx={{
              background: ORANGE_GRADIENT,
              color: '#FFFFFF',
              borderRadius: '10px',
              fontWeight: 700,
              fontSize: 14,
              px: 3,
              py: 1.125,
              textTransform: 'none',
              '&:hover': { background: ORANGE_GRADIENT, filter: 'brightness(1.08)' },
              '&.Mui-disabled': { background: '#E5E7E0', color: TEXT_MUTED },
            }}
          >
            {addingWebsite ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Add Website'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setDeletingWebsite(null); }}
        PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}
      >
        <DialogTitle sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700 }}>Remove Website</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove <strong>{deletingWebsite?.domain?.replace(/^https?:\/\//, '')}</strong>?
            <br /><br />
            {/* Client feedback (Aug 8). "This action cannot be undone." was
                dropped along with the old sentence rather than kept after it:
                the replacement copy ends by promising the site can be
                reconnected at any time, so the two together contradicted each
                other. Removal is a soft delete server-side (deleteWebsite sets
                active = false and keeps the row), so "you can reconnect" is the
                accurate half of that pair.
                OLD: This will disconnect your editorial content from this domain. This action cannot be undone. */}
            You will no longer be able to generate content on this site. The plugin stays
            installed, and you can reconnect the site at any time.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
          <Button onClick={() => { setDeleteDialogOpen(false); setDeletingWebsite(null); }} sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>
            Keep Website
          </Button>
          <Button onClick={handleDeleteWebsite} color="error" variant="contained" disabled={!!deletingId} sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}>
            {deletingId ? <CircularProgress size={18} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── At the plan's website limit ──────────────────────────────────────
          Shown in place of the add form, so the customer learns the limit
          before typing a url rather than after submitting one. The wording is
          built in utils/websiteLimitCopy; nothing about it is decided here. */}
      <Dialog
        open={limitDialogOpen}
        onClose={() => setLimitDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}
      >
        <DialogTitle sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 20 }}>
          {limitCopy.title}
        </DialogTitle>
        <DialogContent>
          <RichMessage
            paragraphs={limitCopy.paragraphs}
            spacing={1.5}
            sx={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: TEXT_MUTED }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1, gap: 1 }}>
          {/* Close is the secondary action and sits first, matching the
              Cancel / confirm order of every other dialog on this screen. */}
          <Button
            onClick={() => setLimitDialogOpen(false)}
            sx={{
              bgcolor: '#FFFFFF',
              color: ORANGE,
              border: `1px solid ${ORANGE}`,
              borderRadius: '10px',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: 14,
              px: 3,
              py: 1,
              textTransform: 'none',
              '&:hover': { bgcolor: '#FEF0EA' },
            }}
          >
            Close
          </Button>
          <Button
            onClick={() => { setLimitDialogOpen(false); navigate(PLANS_PATH); }}
            sx={{
              background: ORANGE_GRADIENT,
              color: '#FFFFFF',
              borderRadius: '10px',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: 14,
              px: 3,
              py: 1,
              textTransform: 'none',
              '&:hover': { background: ORANGE_GRADIENT, filter: 'brightness(1.08)' },
            }}
          >
            {/* "Get paid plan" on the free tier, "Upgrade" on a paid one — it
                comes with the copy, so the button and the sentence above it
                cannot start describing different actions. */}
            {limitCopy.ctaLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WebsiteManagement;
